import React from 'react';
import { GitBranch, GitMerge, RefreshCw, CheckCircle2, AlertTriangle, ShieldCheck } from 'lucide-react';

const MethodologyComparison: React.FC = () => {
    return (
        <div className="space-y-8 animate-in fade-in duration-500 max-w-7xl mx-auto pb-12">

            {/* Header */}
            <div className="border-b border-border pb-6">
                <h1 className="text-3xl font-bold text-text-primary flex items-center gap-3">
                    <ShieldCheck className="text-primary" size={32} />
                    Reliability Methodology Comparison
                </h1>
                <p className="text-text-secondary mt-2 text-lg">
                    Why Magnus Core&apos;s recursive physics engine outperforms standard linear FMEA workflows.
                </p>
            </div>

            {/* Comparison Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                {/* 1. AIAG 4th Edition */}
                <div className="bg-card border border-border rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden group">
                    <div className="absolute top-0 left-0 w-2 h-full bg-red-400"></div>
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-3 bg-red-100 rounded-lg text-red-600 font-bold text-xl">V4</div>
                        <h2 className="text-xl font-semibold">AIAG 4th Edition</h2>
                    </div>
                    <p className="text-text-secondary text-sm mb-6 h-12">
                        The traditional &quot;spreadsheet&quot; approach focusing on RPN calculation and threshold-based actions.
                    </p>

                    <div className="space-y-4">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="text-red-500 shrink-0 mt-1" size={18} />
                            <div>
                                <span className="block font-medium text-sm">Linear Process</span>
                                <span className="text-xs text-text-secondary">One-pass brainstorming after design freeze.</span>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="text-red-500 shrink-0 mt-1" size={18} />
                            <div>
                                <span className="block font-medium text-sm">Subjective Scoring</span>
                                <span className="text-xs text-text-secondary">RPN depends on team opinion, not physics.</span>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="text-red-500 shrink-0 mt-1" size={18} />
                            <div>
                                <span className="block font-medium text-sm">Late Detection</span>
                                <span className="text-xs text-text-secondary">validation happens physically, often too late.</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 2. AIAG-VDA */}
                <div className="bg-card border border-border rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden group">
                    <div className="absolute top-0 left-0 w-2 h-full bg-orange-400"></div>
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-3 bg-orange-100 rounded-lg text-orange-600 font-bold text-xl">VDA</div>
                        <h2 className="text-xl font-semibold">AIAG-VDA Harmonized</h2>
                    </div>
                    <p className="text-text-secondary text-sm mb-6 h-12">
                        The 7-step logic chain emphasizing structural usage and Action Priority (AP).
                    </p>

                    <div className="space-y-4">
                        <div className="flex items-start gap-3">
                            <GitBranch className="text-orange-500 shrink-0 mt-1" size={18} />
                            <div>
                                <span className="block font-medium text-sm">Structured Logic</span>
                                <span className="text-xs text-text-secondary">Structure - Function - Failure trees.</span>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="text-orange-500 shrink-0 mt-1" size={18} />
                            <div>
                                <span className="block font-medium text-sm">Manual Updates</span>
                                <span className="text-xs text-text-secondary">Still relies on human updates for lessons learned.</span>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <CheckCircle2 className="text-green-500 shrink-0 mt-1" size={18} />
                            <div>
                                <span className="block font-medium text-sm">Action Priority</span>
                                <span className="text-xs text-text-secondary">Better prioritization than simple RPN.</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 3. Magnus Recrusive */}
                <div className="bg-card border-2 border-primary/20 rounded-xl p-6 shadow-lg relative overflow-hidden transform scale-105 z-10">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-purple-500"></div>
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-3 bg-primary/10 rounded-lg text-primary font-bold text-xl">
                            <RefreshCw size={24} className="animate-spin-slow" />
                        </div>
                        <h2 className="text-xl font-bold text-primary">Magnus Recursive</h2>
                    </div>
                    <p className="text-text-secondary text-sm mb-6 h-12">
                        Physics-gated, AI-driven loops that evolve the design *before* a prototype is built.
                    </p>

                    <div className="space-y-4">
                        <div className="flex items-start gap-3">
                            <CheckCircle2 className="text-primary shrink-0 mt-1" size={18} />
                            <div>
                                <span className="block font-medium text-sm">Physics Gated</span>
                                <span className="text-xs text-text-secondary">Impossible designs are rejected automatically.</span>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <RefreshCw className="text-primary shrink-0 mt-1" size={18} />
                            <div>
                                <span className="block font-medium text-sm">Recursive Epochs</span>
                                <span className="text-xs text-text-secondary">Failures become new constraints for N+1 generation.</span>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <GitMerge className="text-primary shrink-0 mt-1" size={18} />
                            <div>
                                <span className="block font-medium text-sm">Living Memory</span>
                                <span className="text-xs text-text-secondary">Field data automates risk score updates.</span>
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            {/* Aerator Case Study Visualization */}
            <div className="mt-12 bg-card border border-border rounded-xl p-8">
                <h3 className="text-2xl font-bold mb-6">Case Study: Aerator Pro Development</h3>

                <div className="relative">
                    {/* Connection Line */}
                    <div className="absolute left-8 top-10 bottom-10 w-0.5 bg-gray-200 dark:bg-gray-700"></div>

                    {/* Epoch 1 */}
                    <div className="relative pl-24 pb-12">
                        <div className="absolute left-0 top-0 w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center font-bold text-blue-700 z-10 border-4 border-background">E1</div>
                        <div className="flex flex-col md:flex-row gap-6">
                            <div className="flex-1">
                                <h4 className="font-bold text-lg mb-2">Epoch 1: Initial Concept</h4>
                                <p className="text-text-secondary text-sm">AI generates standard motor design.</p>
                            </div>
                            <div className="flex-1 bg-red-50 dark:bg-red-900/10 p-4 rounded-lg border border-red-100 dark:border-red-900/30">
                                <span className="text-xs font-bold text-red-600 uppercase mb-1 block">Physics Gate Failure</span>
                                <p className="text-sm font-mono text-red-800 dark:text-red-300">Analysis: Temp @ 5000rpm {'>'} 120°C (Limit: 105°C)</p>
                            </div>
                        </div>
                    </div>

                    {/* Recursion Arrow */}
                    <div className="relative pl-24 pb-12">
                        <div className="absolute left-6 top-0 p-1 bg-background z-10">
                            <RefreshCw size={20} className="text-text-secondary" />
                        </div>
                        <p className="text-sm italic text-text-secondary">Recursion: Failure converted into new Constraint</p>
                    </div>

                    {/* Epoch 2 */}
                    <div className="relative pl-24">
                        <div className="absolute left-0 top-0 w-16 h-16 bg-green-100 rounded-full flex items-center justify-center font-bold text-green-700 z-10 border-4 border-background">E2</div>
                        <div className="flex flex-col md:flex-row gap-6">
                            <div className="flex-1">
                                <h4 className="font-bold text-lg mb-2">Epoch 2: Optimized Design</h4>
                                <p className="text-text-secondary text-sm">AI regenerates with constraint: <code className="bg-muted px-1 rounded">Max_RPM {"<"} 4200</code></p>
                            </div>
                            <div className="flex-1 bg-green-50 dark:bg-green-900/10 p-4 rounded-lg border border-green-100 dark:border-green-900/30">
                                <span className="text-xs font-bold text-green-600 uppercase mb-1 block">Physics Gate Pass</span>
                                <p className="text-sm font-mono text-green-800 dark:text-green-300">Analysis: Temp stable at 85°C. Risk Eliminated.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Detailed Table */}
            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse mt-8">
                    <thead>
                        <tr className="border-b border-border">
                            <th className="py-4 px-2 font-bold text-text-primary">Feature</th>
                            <th className="py-4 px-2 font-medium text-text-secondary">Standard (AIAG/VDA)</th>
                            <th className="py-4 px-2 font-bold text-primary">Magnus Recursive</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr className="border-b border-border/50">
                            <td className="py-4 px-2 font-medium">Trigger</td>
                            <td className="py-4 px-2 text-text-secondary">Design Freeze / Change Request</td>
                            <td className="py-4 px-2">Physics Violation or Margin Erosion</td>
                        </tr>
                        <tr className="border-b border-border/50">
                            <td className="py-4 px-2 font-medium">Validation</td>
                            <td className="py-4 px-2 text-text-secondary">Physical DVP&R (Post-prototype)</td>
                            <td className="py-4 px-2">Virtual Physics Lab (Pre-prototype)</td>
                        </tr>
                        <tr className="border-b border-border/50">
                            <td className="py-4 px-2 font-medium">Memory</td>
                            <td className="py-4 px-2 text-text-secondary">Static PDF / Excel files</td>
                            <td className="py-4 px-2">Live Vector Database (Graph)</td>
                        </tr>
                        <tr>
                            <td className="py-4 px-2 font-medium">Human Role</td>
                            <td className="py-4 px-2 text-text-secondary">Data Entry & Scoring</td>
                            <td className="py-4 px-2">Constraint Definition & Review</td>
                        </tr>
                    </tbody>
                </table>
            </div>

        </div>
    );
};

export default MethodologyComparison;
